import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import { useTeamsStore } from './teams'
import { writeAuditLog } from './auditLog'
import { TeamStatus } from '@/types'
import type { Iwi } from '@/types'

export const useIwiStore = defineStore('iwi', () => {
  const activeIwi = ref<Iwi | null>(null)
  const resolvedIwis = ref<Iwi[]>([])
  const usedEntryIds = ref<Set<string>>(new Set())
  const isLoading = ref(false)

  let unsubscribe: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribeToIwi(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubscribe) return
    if (unsubscribe) unsubscribe()
    _incidentId = incidentId
    _opId = opId

    isLoading.value = true
    activeIwi.value = null
    resolvedIwis.value = []
    usedEntryIds.value = new Set()

    unsubscribe = onSnapshot(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'iwi'),
      (snap) => {
        const ids = new Set<string>()
        let found: Iwi | null = null
        const resolved: Iwi[] = []
        for (const d of snap.docs) {
          const data = d.data()
          if (data.triggering_radio_log_entry_id) ids.add(data.triggering_radio_log_entry_id)
          if (data.status === 'active') found = { iwi_id: d.id, ...data } as Iwi
          if (data.status === 'resolved') resolved.push({ iwi_id: d.id, ...data } as Iwi)
        }
        activeIwi.value = found
        resolvedIwis.value = resolved.sort((a, b) => (b.resolved_at?.toMillis() ?? 0) - (a.resolved_at?.toMillis() ?? 0))
        usedEntryIds.value = ids
        isLoading.value = false
      },
    )
  }

  function unsubscribeFromIwi() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
  }

  async function createIwi(
    incidentId: string,
    opId: string,
    payload: {
      declared_by_team_id: string
      declared_by_team_identifier: string
      triggering_radio_log_entry_id: string | null
    },
  ) {
    const teamsStore = useTeamsStore()

    const docRef = await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'iwi'),
      {
        status: 'active',
        declared_at: serverTimestamp(),
        declared_by_team_id: payload.declared_by_team_id,
        declared_by_team_identifier: payload.declared_by_team_identifier,
        triggering_radio_log_entry_id: payload.triggering_radio_log_entry_id,
        chief_complaint: null,
        severity_life_threatening: null,
        patient_location: null,
        point_of_contact: null,
        transport: null,
        pickup_location: null,
        patient_name: null,
        patient_age: null,
        patient_sex: null,
        patient_agency: null,
        team_number: null,
        emt_with_patient: null,
        emt_name: null,
        checklist_team_leader_contacted: false,
        checklist_ops_notified: false,
        checklist_support_requested: false,
        checklist_team_leader_is_poc: false,
        checklist_air_ground_channel: false,
        checklist_command_channel_cleared: false,
        resolved_at: null,
        resolved_by_user_id: null,
        resolved_by_name: null,
      },
    )

    await teamsStore.updateTeamStatus(
      incidentId,
      opId,
      payload.declared_by_team_id,
      TeamStatus.Emergency,
    )

    await writeAuditLog(incidentId, 'iwi.declared', 'iwi', docRef.id, null, {
      team_identifier: payload.declared_by_team_identifier,
    })

    return docRef.id
  }

  async function updateIwiField(
    incidentId: string,
    opId: string,
    iwiId: string,
    field: string,
    value: string | boolean | null,
  ) {
    const before = activeIwi.value ? { [field]: (activeIwi.value as Record<string, unknown>)[field] ?? null } : null

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iwi', iwiId),
      { [field]: value },
    )

    await writeAuditLog(incidentId, 'iwi.field_updated', 'iwi', iwiId, before, { [field]: value })
  }

  async function toggleChecklist(
    incidentId: string,
    opId: string,
    iwiId: string,
    item: string,
  ) {
    if (!activeIwi.value) return

    const currentValue = (activeIwi.value as Record<string, unknown>)[item] as boolean
    const newValue = !currentValue

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iwi', iwiId),
      { [item]: newValue },
    )

    await writeAuditLog(
      incidentId,
      'iwi.checklist_updated',
      'iwi',
      iwiId,
      { [item]: currentValue },
      { [item]: newValue },
    )
  }

  async function resolveIwi(incidentId: string, opId: string, iwiId: string) {
    const authStore = useAuthStore()
    const teamsStore = useTeamsStore()
    const user = authStore.currentUser
    const profile = authStore.userProfile
    const userName = profile
      ? `${profile.user_first_name} ${profile.user_last_name}`.trim()
      : 'Unknown'

    const teamId = activeIwi.value?.declared_by_team_id ?? null

    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'iwi', iwiId),
      {
        status: 'resolved',
        resolved_at: serverTimestamp(),
        resolved_by_user_id: user?.uid ?? null,
        resolved_by_name: userName,
      },
    )

    if (teamId) {
      await teamsStore.updateTeamStatus(incidentId, opId, teamId, TeamStatus.Returned)
    }

    await writeAuditLog(
      incidentId,
      'iwi.resolved',
      'iwi',
      iwiId,
      { status: 'active' },
      { status: 'resolved', resolved_by_name: userName },
    )
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    _incidentId = ''
    _opId = ''
    activeIwi.value = null
    resolvedIwis.value = []
    usedEntryIds.value = new Set()
    isLoading.value = false
  }

  return {
    activeIwi,
    resolvedIwis,
    usedEntryIds,
    isLoading,
    subscribeToIwi,
    unsubscribeFromIwi,
    createIwi,
    updateIwiField,
    toggleChecklist,
    resolveIwi,
    teardown,
  }
})
